import React, {  useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';

import { animated, useSpring } from '@react-spring/web';
import { useDataStore } from 'files/StateManagement.js';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import Account from 'classes/Account.js';
import { AccountComponent } from 'managers/Users.js';
import AnimatedElement from 'views/AnimatedElement.js';
import Appearance from 'styles/Appearance.js';
import { AsyncInteractionItem } from 'managers/Components.js';
import Button from 'views/Button.js';
import Checkbox from 'views/Checkbox.js';
import { DataVault, getNodeIcon } from 'managers/DataVault.js';
import FieldMapper from 'views/FieldMapper.js';
import Layer, { LayerItem, LayerItemSpacing } from 'structure/Layer.js';
import { PanelContentContainer } from 'structure/Panel.js';
import Request from 'files/Request.js';
import { RequiemUserKnowledgeChat } from 'managers/Components.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

const AccessGrantAccountDetails = ({ token }, { abstract, index, options, utils }) => {

    const layerID = `access_grants.${abstract.getID()}`
    const twoFactorCounter = useRef(null);

    const [accessGrant, setAccessGrant] = useState(abstract.object);
    const [layerState, setLayerState] = useState(null);
    const { sockets } = useDataStore();
    const [twoFactorCode, setTwoFactorCode] = useState(null);
    const [twoFactorExpirationDate, setTwoFactorExpirationDate] = useState(null);
    const [twoFactorSecondsRemaining, setTwoFactorSecondsRemaining] = useState(0);

    const onCancelSecureLoginClick = () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // submit request to server
                    await Request.post(utils, '/secure_portal/', {
                        id: accessGrant.id,
                        token: token,
                        type: 'cancel_two_factor_login'
                    });

                    // clear expiration date from local state
                    setTwoFactorExpirationDate(null);

                    // end loading and notify origin that promise has been resolved
                    resolve({
                        title: 'Secure Login Cancelled',
                        message: `Secure login for this account has been successfully cancelled. LifeWipe will no longer allow login attempts for this account to be processed.`
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue cancelling secure login. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Secure Login',
                message: `Secure login is active and can be cancelled at any time. Cancelling secure login will prevent any login attempts from being processed for this linked account.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Cancel Secure Login',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Close',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onNewAccountTwoFactorSMSCode = evt => {
        console.log(evt);
        setTwoFactorCode(evt.code);
    }

    const onOpenAccountWebsite = () => {
        let url = accessGrant.target?.provider?.url;
        if(url) {
            window.open(url);
        }
    }

    const onOpenAccountWebsiteClick = () => {
        return new Promise(resolve => {
            onOpenAccountWebsite();
            resolve();
        });
    }

    const onStartSecureLoginClick = () => {
        return new Promise(async (resolve, reject) => {
            try {

                // prepare target data object for grant 
                let target = accessGrant.data.transfer_account_ownership || accessGrant.data.grant_account_access;

                // determine if two factor authentication is required for account login
                if(target.config.two_factor_required) {
                    let result = await onRequestTwoFactorCode();
                    resolve(result);
                    return;
                }

                // show alert informing user that website will be opened when they are ready
                utils.alert.show({
                    title: 'Start Secure Login',
                    message: `You can now open the account’s website to begin the sign-in process. Follow the prompts on the website to complete the login.`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Open Website',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            onOpenAccountWebsite();
                            resolve();
                            return;
                        }
                        let error = new Error('user_cancelled');
                        reject(error);
                    }
                });

            } catch(e) {
                reject(e);
            }
        });
    }
    
    const onRequestTwoFactorCode = async () => {
        return new Promise((resolve, reject) => {

            let target = accessGrant.data.transfer_account_ownership || accessGrant.data.grant_account_access;

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // submit request to server
                    let { expiration_date } = await Request.post(utils, '/secure_portal/', {
                        id: accessGrant.id,
                        token: token,
                        type: 'start_two_factor_login'
                    });

                    // set expiration date for two factor flow
                    setTwoFactorExpirationDate(moment.utc(expiration_date).local());

                    // end loading and notify origin that promise has been resolved
                    resolve({
                        title: 'Secure Login Ready',
                        message: `Secure login for this account is ready. You can now open the account’s website to begin the sign-in process. We will securely retrieve your two-factor authentication code once it’s sent and display it here for you to enter. When prompted on the website, select the following phone number to receive your code:`,
                        content: (
                            <span style={{
                                ...Appearance.textStyles.title(),
                                display: 'block',
                                marginBottom: 24,
                                textAlign: 'center',
                                whiteSpace: 'normal',
                                width: '100%'
                            }}>{Utils.format.phoneNumber(target.config.two_factor_phone_number)}</span>
                        ),
                        buttons: [{
                            key: 'confirm',
                            title: 'Open Website',
                            style: 'default'
                        },{
                            key: 'cancel',
                            title: 'Cancel',
                            style: 'cancel'
                        }],
                        onClick: key => {
                            if(key === 'confirm') {
                                onOpenAccountWebsite();
                                return; 
                            }
                        } 
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue starting secure login. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Start Secure Login',
                message: `This account is protected by two-factor authentication, enabled by the owner for security. LifeWipe will securely retrieve the verification code and display it here when it’s ready. You’ll then enter the code manually to finish signing in.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onUpdateTwoFactorCounter = () => {

        // clear any pending countdowns if applicable
        if(twoFactorCounter.current) {

            // clear interval and reset seconds remaining
            clearInterval(twoFactorCounter.current);
            setTwoFactorSecondsRemaining(0);
            twoFactorCounter.current = null;

            // remove connection listener for new 2fa codes
            sockets.off('secure_portal', 'new_account_two_factor_sms_code', onNewAccountTwoFactorSMSCode);
        }

        // create a new counter if applicable
        if(twoFactorExpirationDate) {

            // add connection listener for new 2fa codes
            sockets.on('secure_portal', 'new_account_two_factor_sms_code', onNewAccountTwoFactorSMSCode);

            // calculate diff seconds between now and the expiration date
            let secondsRemaining = twoFactorExpirationDate.diff(moment(), 'seconds');

            // set new interval to update counter every second
            twoFactorCounter.current = setInterval(() => {

                // update seconds counter and local state
                secondsRemaining--;
                setTwoFactorSecondsRemaining(secondsRemaining);

                // end counter if applicable
                if(secondsRemaining <= 0) {
                    clearInterval(twoFactorCounter.current);
                    twoFactorCounter.current = null;
                }
            }, 1000);
        }
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {

        // prepare list of default sections
        let sections = [{
            collapsed: false,
            key: 'access_grant',
            title: 'Access Grant',
            items: [{
                description: 'The date that this access grant will expire and no longer provide access to the associated account data.',
                key: 'expiration_date',
                requiemSmartFlow: true,
                title: 'Expiration Date',
                value: accessGrant.expiration_date && Utils.format.date(accessGrant.expiration_date),
                visible: accessGrant.expiration_date ? true : false
            },{
                description: 'The unique identifier for this access grant within the LifeWipe system.',
                key: 'id',
                requiemSmartFlow: true,
                title: 'ID',
                value: `AG-${abstract.getID()}`
            },{
                description: 'The date that this access grant will begin and provide access to the associated account data.',
                key: 'start_date',
                requiemSmartFlow: true,
                title: 'Start Date',
                value: accessGrant.start_date && Utils.format.date(accessGrant.start_date),
                visible: accessGrant.activation_date === null && accessGrant.start_date ? true : false
            },{
                description: 'The amount of time remaining until this access grant expires and no longer provides access to the associated account data.',
                key: 'duration_remaining',
                requiemSmartFlow: true,
                title: 'Time Until Expiration',
                value: Utils.parseDuration(accessGrant.duration_remaining || 0, { largestUnitOnly: true }),
                visible: accessGrant.duration_remaining > 0 ? true : false
            }]
        },{
            collapsed: false,
            key: 'account',
            lastItem: false,
            title: 'Linked Account',
            items: [{
                key: 'category',
                title: 'Category',
                value: accessGrant.target.category?.title
            },{
                key: 'id',
                title: 'ID',
                value: `ACT${accessGrant.target.id}`
            },{
                key: 'name',
                title: 'Name',
                value: accessGrant.target.name
            },{
                key: 'provider',
                title: 'Provider',
                value: accessGrant.target.provider?.title
            }]
        }];

        // determine if account access has been granted
        if(accessGrant.data.transfer_account_ownership || accessGrant.data.grant_account_access) {

            let target = accessGrant.data.transfer_account_ownership || accessGrant.data.grant_account_access;
            sections.push({
                collapsed: false,
                description: accessGrant.data.action.description,
                key: accessGrant.data.action.key,
                lastItem: false,
                title: accessGrant.data.action.title,
                items: [{
                    key: 'username',
                    title: 'Username',
                    value: target.credentials?.username || 'Not available'
                },{
                    key: 'password',
                    isSecure: true,
                    title: 'Password',
                    value: target.credentials?.password || 'Not available'  
                },{
                    key: 'two_factor_required',
                    title: 'Is Two Factor Authentication Enabled?',
                    value: target.config?.two_factor_required ? 'Yes' : 'No'
                },{
                    key: 'mfa_required',
                    title: 'Is Multi Factor Authentication Enabled?',
                    value: target.config?.mfa_required ? 'Yes' : 'No'
                },{
                    key: 'mfa_key',
                    isSecure: true,
                    title: 'Multi Factor Authentication Backup Key',
                    value: target.config?.mfa_key,
                    visible: target.config?.mfa_required || false
                },{
                    key: 'notes',
                    title: 'Notes',
                    value: target.config?.notes
                }]
            });
        }

        return sections;
    }

    const getResources = () => {

        let { grant_account_access, transfer_account_ownership } = accessGrant.data || {}; 
        return (
            <LayerItem 
            collapsed={false}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {(grant_account_access || transfer_account_ownership) && (
                        <AsyncInteractionItem 
                        description={'This will prepare the account to receive a two factor authentication code for the login process.'}
                        image={'images/access-grant-start-account-login-green.png'}
                        onAsyncClick={onStartSecureLoginClick}
                        title={'Start Secure Login'}
                        utils={utils} />
                    )}
                    <AsyncInteractionItem 
                    description={'This will open the website for the provider of the linked account in a new browser tab.'}
                    image={'images/access-grant-open-account-website-blue.png'}
                    lastItem={true}
                    onAsyncClick={onOpenAccountWebsiteClick}
                    title={'Open Account Website'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getTwoFactorCounter = () => {

        let characters = (twoFactorCode || '------').split('');
        return twoFactorSecondsRemaining > 0 && (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                display: 'flex',
                flexDirection: 'column',
                marginBottom: LayerItemSpacing,
                width: '100%'
            }}>
                <AsyncInteractionItem 
                description={`Secure login is active and will deliver verification codes below as they become available. This session will expire at ${twoFactorExpirationDate?.format('h:mm a')}.`}
                image={'images/secure-login-active-yellow.png'}
                lastItem={true}
                onAsyncClick={onCancelSecureLoginClick}
                title={`${Utils.parseDuration(twoFactorSecondsRemaining)} Remaining`}
                utils={utils} />
                <div style={{
                    borderTop: Appearance.styles.bottomBorder(),
                    padding: 16,
                    width: '100%'
                }}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        alignItems: 'center',
                        backgroundColor: Appearance.colors.divider(),
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                        borderBottomLeftRadius: 8,
                        borderBottomRightRadius: 8,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        height: 75,
                        width: '100%'
                    }}>
                        {characters.map((char, index) => {
                            return (
                                <span
                                key={index}
                                style={{
                                    color: Appearance.colors.text(),
                                    fontFamily: "IBM Plex Mono",
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: 28,
                                    paddingLeft: 4,
                                    paddingRight: 4,
                                }}>
                                    {char}
                                </span>
                            )
                        })}
                    </div>
                </div>
            </div>
        )
    }

    useEffect(() => {
        onUpdateTwoFactorCounter();
    }, [twoFactorExpirationDate]);

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            {getTwoFactorCounter()}
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getResources()}
        </Layer>
    )
}

const AccessGrantDataVaultDetails = ({ token }, { abstract, index, options, utils }) => {

    const layerID = `access_grants.${abstract.getID()}`
    const [accessGrant, setAccessGrant] = useState(abstract.object);
    const [layerState, setLayerState] = useState(null);

    const onDataVaultEntryClick = (entry, item) => {

        // determine if item represents a folder
        if(entry.droppable === true) {
            utils.layer.open({
                abstract: abstract,
                Component: DataVaultLayer.bind(this, {
                    token: token,
                }),
                id: 'data_vault_layer'
            });
            return;
        }

        // fallback to showing details for file
        utils.layer.open({
            abstract: Abstract.create({
                object: item,
                type: 'data_vault_items'
            }),
            Component: DataVaultItemDetails.bind(this, { 
                accessGrant: abstract.object,
                token: token 
            }),
            id: `data_vault_items.${item.id}`
        });
    }

    const onOpenDataVaultClick = () => {
        utils.layer.open({
            abstract: abstract,
            Component: DataVaultLayer.bind(this, {
                token: token,
            }),
            id: 'data_vault_layer'
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            key: 'access_grant',
            lastItem: false,
            title: 'Access Grant',
            items: [{
                description: 'The date that this access grant will expire and no longer provide access to the associated data.',
                key: 'expiration_date',
                requiemSmartFlow: true,
                title: 'Expiration Date',
                value: accessGrant.expiration_date && Utils.format.date(accessGrant.expiration_date),
                visible: accessGrant.expiration_date ? true : false
            },{
                description: 'The unique identifier for this access grant within the LifeWipe system.',
                key: 'id',
                requiemSmartFlow: true,
                title: 'ID',
                value: `AG-${abstract.getID()}`
            },{
                description: 'The date that this access grant will begin and provide access to the associated data.',
                key: 'start_date',
                requiemSmartFlow: true,
                title: 'Start Date',
                value: accessGrant.start_date && Utils.format.date(accessGrant.start_date),
                visible: accessGrant.activation_date === null && accessGrant.start_date ? true : false
            },{
                description: 'The amount of time remaining until this access grant expires and no longer provides access to the associated data.',
                key: 'duration_remaining',
                requiemSmartFlow: true,
                title: 'Time Until Expiration',
                value: Utils.parseDuration(accessGrant.duration_remaining || 0, { largestUnitOnly: true }),
                visible: accessGrant.duration_remaining > 0 ? true : false
            }]
        }];
    }

    const getVaultItems = () => {
        let { items, map: mapItems } = accessGrant.data.share_data_vault_items || {};
        return mapItems?.length > 0 && (
            <LayerItem
            collapsed={false}
            lastItem={false}
            title={accessGrant.data.action.title}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {mapItems.map((item, index) => {

                        let target = items.find(i => i.id === item.id);
                        return (
                            Views.row({
                                bottomBorder: index !== mapItems.length - 1,
                                hideOnClickArrow: true,
                                key: index,
                                label: item.text,
                                onClick: onDataVaultEntryClick.bind(this, item, target),
                                prepend: (
                                    <img
                                    src={getNodeIcon({
                                        ...item,
                                        item: target
                                    })}
                                    style={{
                                        height: 20,
                                        marginRight: 8,
                                        objectFit: 'contain',
                                        width: 20
                                    }} />
                                ),
                                value: (
                                    <img
                                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                                    style={{
                                        height: 12,
                                        marginLeft: 12,
                                        objectFit: 'contain',
                                        width: 12
                                    }} />
                                )
                            })
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getResources = () => {
        return abstract.object.data.action.key === 'share_data_vault_items' && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'Explore and interact with the secure data vault items that have been shared with you via this access grant.'}
                    image={'images/secure-data-vault-blue.png'}
                    lastItem={true}
                    onAsyncClick={onOpenDataVaultClick}
                    title={'Open Secure Data Vault'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getVaultItems()}
            {getResources()}
        </Layer>
    )
}

const AccessGrantRequiemDetails = ({ token }, { abstract, index, options, utils }) => {

    const layerID = `access_grants.${abstract.getID()}`
    const [accessGrant, setAccessGrant] = useState(abstract.object);
    const [layerState, setLayerState] = useState(null);

    const onOpenRequiemClick = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // fetch requiem access token from sever
                let response = await Request.get(utils, '/secure_portal/', {
                    access_grant_id: accessGrant.id,
                    token: token,
                    type: 'get_requiem_user_knowledge_access_token'
                });

                // open layer for requiem user knowledge chat
                utils.layer.open({
                    Component: RequiemUserKnowledgeChat.bind(this, response),
                    id: 'requiem_user_knowledge_chat'
                });
                resolve({});

            } catch(e) {
                reject(e);
            }
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            key: 'access_grant',
            lastItem: false,
            title: 'Access Grant',
            items: [{
                description: 'The date that this access grant will expire and no longer provide access to the associated resource.',
                key: 'expiration_date',
                requiemSmartFlow: true,
                title: 'Expiration Date',
                value: accessGrant.expiration_date && Utils.format.date(accessGrant.expiration_date),
                visible: accessGrant.expiration_date ? true : false
            },{
                description: 'The unique identifier for this access grant within the LifeWipe system.',
                key: 'id',
                requiemSmartFlow: true,
                title: 'ID',
                value: `AG-${abstract.getID()}`
            },{
                description: 'The date that this access grant will begin and provide access to the associated resource.',
                key: 'start_date',
                requiemSmartFlow: true,
                title: 'Start Date',
                value: accessGrant.start_date && Utils.format.date(accessGrant.start_date),
                visible: accessGrant.activation_date === null && accessGrant.start_date ? true : false
            },{
                description: 'The amount of time remaining until this access grant expires and no longer provides access to the associated resource.',
                key: 'duration_remaining',
                requiemSmartFlow: true,
                title: 'Time Until Expiration',
                value: Utils.parseDuration(accessGrant.duration_remaining || 0, { largestUnitOnly: true }),
                visible: accessGrant.duration_remaining > 0 ? true : false
            }]
        }];
    }

    const getResources = () => {
        return abstract.object.data.action.key === 'grant_requiem_access' && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'Explore and interact with the AI model shaped by the account holder’s shared memories, language, preferences, and digital history.'}
                    image={'images/requiem-ai-green.png'}
                    lastItem={true}
                    onAsyncClick={onOpenRequiemClick}
                    title={'Open Requiem AI'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    return (
        <Layer 
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState
        }}>
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getResources()}
        </Layer>
    )
}

export const DataVaultItemDetails = ({ accessGrant, token }, { abstract, index, options, utils }) => {

    const layerID = `data_vault_items.${abstract.getID()}`
    const [layerState, setLayerState] = useState(null);

    const onDownloadItem = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit request to server
                let { url } = await Request.get(utils, '/secure_portal/', {
                    access_grant_id: accessGrant.id,
                    id: abstract.getID(),
                    token: token,
                    type: 'download_data_vault_item'
                });

                // end loading and notify origin that promise has been resolved
                resolve({ 
                    buttons: [{
                        key: 'download',
                        title: 'Download',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    message: `Your secure link for ${abstract.object.name} has been prepared and is ready for download.`,
                    onClick: key => {
                        if(key === 'download') {
                            window.open(url);
                            return;
                        }
                    }
                });
    
            } catch(e) {
                let error = new Error(`There was an issue preparing your secure download link. ${e.message || 'An unknown error occurred'}`);
                reject(error);
            }
        });
    }

    const getResources = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Resources'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={'Securely download a copy of this item from the Data Vault to your computer or mobile device.'}
                    image={'images/download-data-vault-item.png'}
                    lastItem={true}
                    onAsyncClick={onDownloadItem}
                    title={'Download Copy'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    const getFields = () => {
        return [{
            collapsed: false,
            lastItem: false,
            title: 'About This File',
            items: [{
                key: 'date',
                title: 'Created',
                value: Utils.format.date(abstract.object.date)
            },{
                key: 'description',
                title: 'Description',
                value: abstract.object.metadata.description
            },{
                key: 'name',
                title: 'Name',
                value: abstract.object.name
            },{
                key: 'ai_enabled',
                title: 'Use with Requiem',
                value: abstract.object.ai_enabled ? 'Yes' : 'No'
            }]
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'medium'
        }}>
            <FieldMapper
            sections={getFields()}
            utils={utils} />
            {getResources()}
        </Layer>
    )
}

export const DataVaultLayer = ({ token }, { abstract, index, options, utils }) => {

    const layerID = 'data_vault_layer';
    const dataVaultRef = useRef(null);
    const [layerState, setLayerState] = useState(null);

    const onItemClick = item => {
        utils.layer.open({
            abstract: Abstract.create({
                object: item,
                type: 'data_vault_items'
            }),
            Component: DataVaultItemDetails.bind(this, { 
                accessGrant: abstract.object,
                token: token 
            }),
            id: `data_vault_items.${item.id}`
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            sizing: 'large'
        }}
        utils={utils}>
            <LayerItem
            collapsed={false}
            title={'Data Vault Browser'}>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    display: 'block',
                    marginBottom: 12
                }}>{`You are viewing files and folders that were intentionally shared with you by the account holder. These items are preserved exactly as they were provided—no edits, uploads, or deletions are allowed. Click on any folder to explore its contents, or open individual files to review them. All content is read-only and access is time-limited per the account holder’s directives.`}</span>
            </LayerItem>
            <DataVault
            accessGrant={abstract.object}
            onItemClick={onItemClick}
            ref={dataVaultRef}
            utils={utils} />
        </Layer>
    )
}

export const SecurePortal = ({ utils }) => {

    const expirationCounter = useRef(null);

    const [accounts, setAccounts] = useState([]);
    const [blueprints, setBlueprints] = useState({});
    const [consentVal, setConsentVal] = useState(false);
    const [descriptionVisible, setDescriptionVisible] = useState(true);
    const [devices, setDevices] = useState([]);
    const [expirationAnimations, expirationAnimationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -25
    }));
    const [expirationDate, setExpirationDate] = useState();
    const [expirationSecondsRemaining, setExpirationSecondsRemaining] = useState(0);
    const [loading, setLoading] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(null);
    const [publicToken, setPublicToken] = useState(null);
    const { request, sockets } = useDataStore();
    const [system_triggers, setSystemTriggers] = useState([]);
    const [token, setToken] = useState();

    const onAccessGrantClick = grant => {
        utils.layer.open({
            abstract: Abstract.create({
                object: grant,
                type: 'access_grants'
            }),
            Component: AccessGrantAccountDetails.bind(this, { token }),
            id: `access_grants.${grant.id}`
        });
    }

    const onContinueClick = () => {
        try {

            if(!publicToken) {
                throw new Error('The link you used to access this page is invalid, incomplete, or expired. Please check the link and try again, or contact the account owner for a new link if the issue persists.');
            }
            setDescriptionVisible(false);

        } catch(e) {
            utils.alert.error({
                error: e,
                message: e.message,
                origin: 'secure_portal.on_continue_click'
            });
        }
    }

    const onDataVaultAccessGrantClick = grant => {
        utils.layer.open({
            abstract: Abstract.create({
                object: grant,
                type: 'access_grants'
            }),
            Component: AccessGrantDataVaultDetails.bind(this, { token }),
            id: `access_grants.${grant.id}`
        });
    }

    const onRequiemAccessGrantClick = grant => {
        utils.layer.open({
            abstract: Abstract.create({
                object: grant,
                type: 'access_grants'
            }),
            Component: AccessGrantRequiemDetails.bind(this, { token }),
            id: `access_grants.${grant.id}`
        });
    }

    const onSendCodeClick = async () => {
        try {

            let code = null;

            // prevent moving forward if no phone number was provided
            if(!phoneNumber) {   
                throw new Error('Please provide a phone number to receive a verification code via text message (SMS) by entering it below.');
            }

            // prevent moving forward if consent value was not given
            if(!consentVal) {   
                throw new Error('Please provide consent to receive a verification code via text message (SMS) by checking the box before continuing.');
            }

            // set loading flag and submit request to server
            setLoading('send_code');
            await Request.get(utils, '/secure_portal/', {
                phone_number: phoneNumber,
                public_token: publicToken,
                type: 'new_two_factor_authentication_code'
            });

            // show alert requesting two factor verification code
            setLoading(false);
            utils.alert.show({
                title: 'Verification Required',
                message: `We've sent a verification code to the provided phone number. Please enter the verification code below to continue.`,
                content: (
                    <div style={{
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        width: '100%'
                    }}>
                        <TextField
                        fieldStyle={{
                            textAlign: 'center'
                        }}
                        onChange={text => code = text}
                        placeholder={'Verification Code'} />
                    </div>
                ),
                buttons: [{
                    key: 'continue',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: async key => {
                    await Utils.sleep(0.5);
                    if(code && key === 'continue') {
                        onVerifyTwoFactorCode(code);
                        return;
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue sending your verification code. ${e.message || 'An unknown error occurred.'}`,
                origin: 'secure_portal.on_send_code_click'
            });
        }
    }

    const onUpdateExpirationCounter = () => {

        // clear any pending countdowns if applicable
        if(expirationCounter.current) {
            clearInterval(expirationCounter.current);
            setExpirationSecondsRemaining(0);
            expirationCounter.current = null;

            // animate components out of view
            expirationAnimationsApi.start({ 
                opacity: 0,
                top: -25 
            });
        }

        // create a new counter if applicable
        if(expirationDate) {

            // calculate diff seconds between now and the expiration date
            let seconds = expirationDate.diff(moment(), 'seconds');
            let secondsRemaining = seconds + 0;

            // set new interval to update counter every second
            expirationCounter.current = setInterval(() => {

                // animate components into view 
                if(secondsRemaining === seconds) {
                    expirationAnimationsApi.start({
                        opacity: 1, 
                        top: -1 
                    });
                }

                // update seconds counter and local state
                secondsRemaining--;
                setExpirationSecondsRemaining(secondsRemaining);

                // end counter if applicable
                if(secondsRemaining <= 0) {
                    clearInterval(expirationCounter.current);
                    expirationCounter.current = null;
                }
            }, 1000);
        }
    }

    const onVerifyTwoFactorCode = async code => {
        try {
            setLoading(true);
            let { expiration_date, token } = await Request.post(utils, '/secure_portal/', {
                code: code,
                public_token: publicToken,
                type: 'verify_two_factor_authentication_sms'
            });

            console.log(token);

            // end loading and update state with session token and expiration date
            setLoading(false);
            setExpirationDate(moment.utc(expiration_date).local());
            setToken(token);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue verifying your code. ${e.message || 'An unknown error occurred.'}`,
                origin: 'secure_portal.on_verify_two_factor_code'
            });
        }
    }

    const getAccounts = () => {
        return accounts?.length > 0 && (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`You are viewing the linked accounts that the original account holder intentionally chose to share with you through LifeWipe. This list represents only the information the owner approved for release—nothing more, nothing less. Each entry in this list has been securely transferred into your portal access. You may review, download, or act on these accounts according to the permissions granted. All activity is logged for transparency and cannot be altered retroactively.`}
                title={'Linked Accounts'}>
                    <div 
                    className={'text-left text-xl-center'}
                    style={{
                        ...Utils.isMobile() === false && Appearance.styles.unstyledPanel(),
                        alignItems: 'center',
                        borderRadius: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        maxWidth: 1250,
                        width: '100%'
                    }}>
                        {accounts.map((grant, index, accounts) => {
                            return (
                                <AccountComponent 
                                account={grant.target}
                                bottomBorder={index !== accounts.length - 1} 
                                key={index} 
                                onClick={onAccessGrantClick.bind(this, grant)} />
                            )
                        })}
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        )
    }

    const getBlueprints = () => {

        // no additional logic is required if no blueprints were found
        let keys = Object.keys(blueprints);
        if(keys.length === 0) {
            return null;
        }

        // loop through targets and render section for each category
        return keys.map(key => {
            if(key === 'data_vault') {
                return (
                    <div 
                    key={key}
                    style={{
                        width: '100%'
                    }}>
                        {getDataVaultItems(blueprints[key])}
                    </div>
                )
            }
            if(key === 'requiem') {
                return (
                    <div 
                    key={key}
                    style={{
                        width: '100%'
                    }}>
                        {getRequiemItems(blueprints[key])}
                    </div>
                )
            }
            return null;
        });
    }

    const getBlueprintIcon = key => {
        switch(key) {
            case 'data_vault': 
            return window.theme === 'dark' ? 'images/secure-portal-data-vault-dark.png' : 'images/secure-portal-data-vault-light.png';

            case 'requiem': 
            return window.theme === 'dark' ? 'images/secure-portal-requiem-dark.png' : 'images/secure-portal-requiem-light.png';

            default: 
            return window.theme === 'dark' ? 'images/blueprint-icon-dark.png' : 'images/blueprint-icon-light.png';
        }
    }

    const getContainerButtons = () => {
        return [{
            color: 'primary',
            id: 'continue',
            onClick: onContinueClick,
            text: 'Continue'
        }];
    }

    const getContent = () => {
        if(descriptionVisible === true) {
            return (
                <AnimatedElement
                {...utils.animations.get('panel')}
                animateWithPage={true}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <PanelContentContainer
                    body={`As part of LifeWipe’s commitment to data dignity and control, we’ve created a Secure Portal that allows designated individuals—such as trusted contacts—to view specific digital content the original user chose to preserve and share. LifeWipe does not allow open-ended browsing of a deceased user's data. This portal only reveals what was intentionally preserved for you—nothing more, nothing less. Every session is logged, encrypted, and expires automatically after a brief window of access.`}
                    buttons={getContainerButtons()}
                    title={'Secure Portal'} />
                </AnimatedElement>
            )
        }
        if(!token) {
            return (
                <AnimatedElement
                {...utils.animations.get('panel')}
                animateWithPage={true}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <PanelContentContainer
                    body={`For your security and to respect the privacy of the original account holder, we require two-factor authentication before granting access. Please enter your mobile number below. We’ll send a one-time code to confirm that you are the intended recipient of this data access. This should be the same phone number the account holder used when they setup your access.`}
                    childrenStyle={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    title={'Verify Your Identity'}>
                        <div style={{
                            ...Appearance.styles.unstyledPanel(),
                            alignSelf: 'center',
                            maxWidth: 500,
                            width: '100%'
                        }}>
                            <div style={{
                                alignItems: 'center',
                                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                                display: 'flex',
                                flexDirection: 'row',
                                minWidth: 0,
                                padding: 24,
                                width: '100%'
                            }}>
                                <TextField
                                format={'phone_number'}
                                onChange={setPhoneNumber}
                                placeholder={'Mobile Number'} />
                                <Button
                                color={'primary'}
                                containerStyle={{
                                    marginLeft: 12
                                }}
                                label={'Send Code'}
                                loading={loading === 'send_code'}
                                onClick={onSendCodeClick}
                                style={{
                                    height: 35
                                }}
                                type={'large'} />
                            </div>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                minWidth: 0,
                                padding: 24,
                                width: '100%'
                            }}>
                                <Checkbox
                                enabled={true}
                                onChange={setConsentVal} />
                                <span style={{
                                    color: Appearance.colors.subText(),
                                    display: 'block',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    lineHeight: 1.75,
                                    marginLeft: 18,
                                    textAlign: 'left',
                                    width: '100%'
                                }}>{`By checking this box, I consent to receive a one-time text message (SMS) containing a verification code for secure access. I understand that this message is part of LifeWipe’s two-factor authentication process and will not be used for marketing or stored for future contact.`}</span>
                            </div>
                        </div>
                    </PanelContentContainer>
                </AnimatedElement>
            )
        }
        return (
            <>
            {getAccounts()}
            {getBlueprints()}
            </>
        )
    }

    const getDataVaultItems = blueprint => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={blueprint.secure_portal_description}
                title={blueprint.title}>
                    <div 
                    className={'text-left'}
                    style={{
                        ...Utils.isMobile() === false && Appearance.styles.unstyledPanel(),
                        borderRadius: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        maxWidth: 1250,
                        width: '100%'
                    }}>
                        {blueprint.grants.length === 0 && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                padding: 16,
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.header(),
                                    fontFamily: 'Zen Dots'
                                }}>{'No Data Vault Items Found'.toUpperCase()}</span>
                                <span style={{
                                    ...Appearance.textStyles.defaultText(),
                                    color: Appearance.colors.subText()
                                }}>{'No eligible Data Vault items were found. The account holder may have revoked access or removed the files from their Data Vault.'}</span>
                            </div>
                        )}
                        {blueprint.grants.map((grant, index, grants) => {

                            // prepare tile value for grant
                            let title = grant.data.action.title;
                            if(grant.data.share_data_vault_items) {
                                title = grant.data.share_data_vault_items.description;
                            }

                            return (
                                <div 
                                className={`view-entry ${window.theme}`}
                                key={index}
                                onClick={onDataVaultAccessGrantClick.bind(this, grant)}
                                style={{
                                    alignItems: 'center',
                                    backgroundColor: null,
                                    borderBottom: index !== grants.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    minWidth: 0,
                                    padding: 16,
                                    width: '100%'
                                }}>
                                    <img 
                                    src={getBlueprintIcon(blueprint.key)}
                                    style={{
                                        height: 50,
                                        marginRight: 16,
                                        objectFit: 'contain',
                                        width: 50
                                    }} />
                                    <div style={{
                                        alignItems: 'flex-start',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.header(),
                                            fontFamily: 'Zen Dots'
                                        }}>{title.toUpperCase()}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            color: Appearance.colors.subText()
                                        }}>{`Created: ${Utils.format.date(grant.date)}`}</span>
                                    </div>
                                    <img
                                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                                    style={{
                                        height: 20,
                                        marginLeft: 12,
                                        objectFit: 'contain',
                                        width: 15
                                    }} />
                                </div>
                            )
                        })}
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        )
    }

    const getExpirationCounter = () => {
        return expirationSecondsRemaining > 0 && (
            <animated.div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                marginTop: 36,
                paddingLeft: '5vw',
                paddingRight: '5vw',
                position: 'relative',
                width: '100%',
                ...expirationAnimations
            }}>
                <div style={{
                    backgroundColor: Appearance.colors.panelBackground(),
                    borderColor: Appearance.colors.divider(),
                    borderBottomLeftRadius: 16,
                    borderBottomRightRadius: 16,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    maxWidth: Utils.isMobile() ? '100%' : 550,
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    <AsyncInteractionItem 
                    description={`To protect the privacy, wishes, and security of the account holder, access is strictly time-limited to 1 hour. This session will expire at ${expirationDate?.format('h:mm a')}.`}
                    image={'images/secure-login-active-yellow.png'}
                    lastItem={true}
                    onClick={onExpirationCounterClick}
                    title={`${Utils.parseDuration(expirationSecondsRemaining)} Remaining`}
                    utils={utils} />
                </div>
            </animated.div>
        )
    }

    const onExpirationCounterClick = () => {
        utils.alert.show({
            title: 'Session Expiration',
            message: `For your security and to respect the privacy of the original account holder, this secure portal session is time-limited and will automatically expire at ${expirationDate?.format('h:mm a')}. Once expired, you will need to re-verify your identity to regain access.`
        });
    }

    const getRequiemItems = blueprint => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={blueprint.secure_portal_description}
                title={blueprint.title}>
                    <div 
                    className={'text-left'}
                    style={{
                        ...Utils.isMobile() === false && Appearance.styles.unstyledPanel(),
                        borderRadius: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        maxWidth: 1250,
                        width: '100%'
                    }}>
                        {blueprint.grants.map((grant, index, grants) => {
                            return (
                                <div 
                                className={`view-entry ${window.theme}`}
                                key={index}
                                onClick={onRequiemAccessGrantClick.bind(this, grant)}
                                style={{
                                    alignItems: 'center',
                                    backgroundColor: null,
                                    borderBottom: index !== grants.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    minWidth: 0,
                                    padding: 16,
                                    width: '100%'
                                }}>
                                    <img 
                                    src={getBlueprintIcon(blueprint.key)}
                                    style={{
                                        height: 50,
                                        marginRight: 16,
                                        objectFit: 'contain',
                                        width: 50
                                    }} />
                                    <div style={{
                                        alignItems: 'flex-start',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.header(),
                                            fontFamily: 'Zen Dots'
                                        }}>{grant.data.action.title.toUpperCase()}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            color: Appearance.colors.subText()
                                        }}>{`Created: ${Utils.format.date(grant.date)}`}</span>
                                    </div>
                                    <img
                                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                                    style={{
                                        height: 20,
                                        marginLeft: 12,
                                        objectFit: 'contain',
                                        width: 15
                                    }} />
                                </div>
                            )
                        })}
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        )
    }

    const fetchAccessGrants = async () => {
        try {
            let { accounts, blueprints, devices, system_triggers } = await Request.get(utils, '/secure_portal/', {
                token: token,
                type: 'access_grants'
            });

            console.log(accounts, blueprints, devices, system_triggers);
            setDevices(devices);
            setSystemTriggers(system_triggers);

            // prepare account objects
            setAccounts(accounts?.map(entry => {
                return {
                    ...entry,
                    date: moment.utc(entry.date).local(),
                    target: Account.create(entry.target)
                }
            }));

            // sort all blueprints into sections based on categories
            let targets = blueprints.reduce((object, blueprint) => {
                if(!object[blueprint.target.key]) {
                    object[blueprint.target.key] = {
                        ...blueprint.target,
                        grants: []
                    };
                }
                object[blueprint.target.key].grants.push({
                    ...blueprint,
                    date: moment.utc(blueprint.date).local()
                });
                return object;
            }, {});

            setBlueprints(targets);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue fetching your access grants. ${e.message || 'An unknown error occurred.'}`,
                origin: 'secure_portal.fetch_access_grants'
            });
        }
    }

    const subscribeToWebSockets = () => {
        sockets.connect({ entity: { token }, url: API.sockets });
    }

    useEffect(() => {
        onUpdateExpirationCounter();
    }, [expirationDate]);

    useEffect(() => {
        if(token) {
            fetchAccessGrants();
            subscribeToWebSockets();
        }
    }, [token]);

    useEffect(() => {
        setPublicToken(request?.token);
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            {getExpirationCounter()}
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '5vw',
                width: '100%'
            }}>
                {getContent()}
            </div>
        </div>
    )
}
